<template>
    <div class="upload-book">
        <Spinner v-if="loading" class="spinner--opaque">{{ loadingText }}</Spinner>
        <h3 class="upload-book__heading">
            {{ $t('upload.title') }}
            <span class="upload-book__notice">&nbsp;{{ $t('upload.format') }}</span>
        </h3>

        <form class="upload-book__form" @submit.prevent="uploadBook">
            <ErrorList :error="error" />
            <FormField name="document" inline :error="error" required>
                <FileInput
                    style="width: 100%;"
                    accept=".docx"
                    :label="$t('upload.fileLabel')"
                    @selected="setDocument"
                />
            </FormField>
            <p>
                <a class="link upload-book__link" href="/docs/prepare" target="_blank">
                    {{ $t('upload.rules') }}
                </a>
            </p>
            <FormField
                name="name"
                :label="$t('upload.name')"
                :error="error"
                required
                inline
                label-width="240px"
            >
                <FormInput
                    v-model="book.name"
                    :placeholder="$t('upload.titlePlaceholder')"
                    theme="editor"
                />
            </FormField>
            <template v-if="$isManageApp && (isStaff || isAdmin)">
                <FormField
                    name="copyright_holder"
                    :label="$t('upload.copyrightHolder')"
                    :error="error"
                    label-width="240px"
                    inline
                >
                    <CustomSelect
                        v-model="book.copyright_holder"
                        emit-prop="id"
                        :form-value-text="item => item.label.rus"
                        :options="copyrightHolders"
                        style="width: 300px;"
                    >
                        <template #default="{ item }">{{ item.label.rus }}</template>
                    </CustomSelect>
                </FormField>
                <FormField name="is_proofing" :error="error">
                    <Checkbox v-model="book.is_proofing">
                        <span style="margin-left: 4px; font-size: 14px; cursor: pointer;">
                            Autoproofing
                        </span>
                    </Checkbox>
                </FormField>
                <FormField
                    v-if="isStaff || isAdmin"
                    name="reviewers"
                    label="Reviewers"
                    inline
                    :error="error"
                    label-width="240px"
                >
                    <MultiSelect
                        v-model="book.reviewers"
                        :options="reviewers"
                        :form-value-text="item => item.email"
                        with-filter
                        async-search
                        emit-prop="id"
                        empty-text="No reviewers"
                        style="width: 300px;"
                        paginate
                        @search="onReviewersSearch"
                    >
                        <template v-slot:default="{ item }">
                            {{ item.email }}
                        </template>
                    </MultiSelect>
                </FormField>
            </template>

            <FormField
                :label="$t('upload.speed')"
                name="speed"
                inline
                :error="error"
                label-width="240px"
            >
                <NumberInput v-model="book.speed" step="0.05" :min="0.3" :max="3" />
            </FormField>

            <FormField
                v-if="isStaff || isAdmin || authors.length > 1"
                :label="$t('misc.author')"
                name="author"
                inline
                :error="error"
                required
                label-width="240px"
            >
                <component
                    :is="authorSelectComponent"
                    v-model="book.authors"
                    :options="authors"
                    :form-value-text="authorName"
                    with-filter
                    emit-prop="id"
                    :empty-text="$t('upload.noAuthors')"
                    style="width: 300px;"
                    paginate
                >
                    <template v-slot:default="{ item }">
                        {{ authorName(item) }}
                    </template>
                </component>
            </FormField>

            <FormField
                :label="$t('misc.language')"
                name="book_language"
                inline
                :error="error"
                label-width="240px"
                required
            >
                <LanguageSelect
                    v-model="book.book_language"
                    :languages="languages"
                    width="100%"
                    @select="onLanguageUpdate"
                />
            </FormField>

            <div class="upload-book__tariffs">
                <LimitsList
                    v-model="limit"
                    class="upload-book__tariffs-wrap"
                    :limits="filteredLimits"
                    :speaker="book.speaker"
                    :selectable="!!book.book_language || !!commonLanguage"
                />
                <div class="upload-book__notice" style="font-size: 12px;">
                    {{ $t('upload.notice') }}
                </div>
            </div>

            <FormField
                :label="$t('misc.voice')"
                name="speaker"
                inline
                :error="error"
                label-width="240px"
                required
            >
                <SpeakerSelect
                    v-model="book.speaker"
                    :show-options="!!book.book_language"
                    :empty-text="
                        book.book_language
                            ? $t('editor.toolbar.noVoices')
                            : $t('upload.selectLanguage')
                    "
                    width="300px"
                />
            </FormField>

            <div v-if="isAdmin || isStaff" class="upload-book__isbn">
                <Checkbox v-model="showIsbn">
                    <span style="margin-left: 4px; font-size: 14px; cursor: pointer;">
                        {{ $t('upload.iHaveIsbn') }}
                    </span>
                </Checkbox>
                <TransitionExpand>
                    <FormField
                        v-if="showIsbn"
                        name="isbnx"
                        label="ISBN"
                        :error="error"
                        required
                        inline
                        label-width="240px"
                    >
                        <FormInput
                            ref="isbn-input"
                            v-model="book.isbnx"
                            :mask="'#############'"
                            placeholder="ISBN"
                            theme="editor"
                        />
                    </FormField>
                </TransitionExpand>
            </div>

            <template v-if="!personalDataFilled">
                <div class="upload-book__heading">
                    <h4>
                        {{ $t('upload.authorData') }}
                    </h4>
                    <button
                        type="button"
                        class="link"
                        style="margin-left: 10px; font-size: 13px; font-weight: normal; border: none;"
                        @click="toggleStayConfidential"
                    >
                        {{
                            stayConfidential
                                ? $t('upload.fillProfile')
                                : $t('upload.stayConfidential')
                        }}
                    </button>
                </div>
                <p v-if="stayConfidential" class="upload-book__notice" style="font-size: 12px">
                    {{ $t('upload.stayConfidentialNotice') }}
                </p>
                <TransitionExpand>
                    <div v-if="!stayConfidential">
                        <FormField
                            name="first_name"
                            :label="$t('misc.firstName')"
                            :error="error"
                            required
                            inline
                            label-width="240px"
                        >
                            <FormInput
                                v-model="profile.first_name"
                                :placeholder="$t('misc.firstName')"
                                theme="editor"
                            />
                        </FormField>
                        <FormField
                            name="last_name"
                            :label="$t('misc.lastName')"
                            :error="error"
                            required
                            inline
                            label-width="240px"
                        >
                            <FormInput
                                v-model="profile.last_name"
                                :placeholder="$t('misc.lastName')"
                                theme="editor"
                            />
                        </FormField>
                        <FormField
                            name="patronymic"
                            :label="$t('misc.patronymic')"
                            :error="error"
                            inline
                            label-width="240px"
                        >
                            <FormInput
                                v-model="profile.patronymic"
                                :placeholder="$t('misc.patronymic')"
                                theme="editor"
                            />
                        </FormField>
                        <FormField
                            name="birth_day"
                            :label="$t('misc.dateOfBirth')"
                            :error="error"
                            inline
                            required
                            label-width="240px"
                        >
                            <DateInput
                                v-model="profile.birth_day"
                                picker-position="top"
                                style="min-width: 300px; margin-bottom: 0"
                            />
                        </FormField>
                        <FormField
                            name="phone"
                            :label="$t('misc.phoneNumber')"
                            :error="error"
                            inline
                            required
                            label-width="240px"
                        >
                            <FormInput
                                v-model="profile.phone"
                                :placeholder="
                                    $i18n.locale === 'rus' ? '+7(___)___-__-__' : 'Phone number'
                                "
                                theme="editor"
                            />
                        </FormField>
                        <p style="margin-top: 0">{{ $t('upload.rightsNotice') }}</p>
                        <FormField
                            name="currency"
                            :label="$t('currency.currency')"
                            :error="error"
                            inline
                            required
                            label-width="240px"
                        >
                            <CustomSelect
                                v-model="profile.currency"
                                :options="$options.currencies"
                                :empty-text="$t('upload.noAuthors')"
                                style="width: 300px;"
                            />
                        </FormField>
                    </div>
                </TransitionExpand>
                <button
                    v-if="!showPseudonym"
                    type="button"
                    class="link"
                    style="margin-left: 10px; font-size: 13px; font-weight: normal; border: none;"
                    @click="showPseudonym = true"
                >
                    {{ $t('upload.addPseudo') }}
                </button>
                <TransitionExpand>
                    <div v-if="showPseudonym">
                        <h4>
                            {{ $t('misc.pseudonym') }}
                            <button
                                type="button"
                                class="link"
                                :class="{
                                    'link--disabled': !profile.last_name || !profile.first_name
                                }"
                                style="margin-left: 10px; font-size: 13px; font-weight: normal; border: none;"
                                @click="copyPseudo"
                            >
                                {{ $t('upload.pseudonymNotice') }}
                            </button>
                        </h4>
                        <FormField
                            name="first_name"
                            :label="$t('misc.firstName')"
                            :error="error"
                            required
                            inline
                            label-width="240px"
                        >
                            <FormInput
                                v-model="author.first_name"
                                :placeholder="$t('misc.firstName')"
                                theme="editor"
                            />
                        </FormField>
                        <FormField
                            name="last_name"
                            :label="$t('misc.lastName')"
                            :error="error"
                            required
                            inline
                            label-width="240px"
                        >
                            <FormInput
                                v-model="author.last_name"
                                :placeholder="$t('misc.lastName')"
                                theme="editor"
                            />
                        </FormField>
                        <FormField
                            name="patronymic"
                            :label="$t('misc.patronymic')"
                            :error="error"
                            inline
                            label-width="240px"
                        >
                            <FormInput
                                v-model="author.patronymic"
                                :placeholder="$t('misc.patronymic')"
                                theme="editor"
                            />
                        </FormField>
                    </div>
                </TransitionExpand>
            </template>
            <div class="editor-form__row" style="justify-content: center;">
                <input
                    :disabled="!valid"
                    :class="{ 'is-disabled': !valid }"
                    type="submit"
                    class="button button--big"
                    :value="$t('upload.upload')"
                />
            </div>
        </form>
    </div>
</template>

<script>
import {
    FormField,
    FormInput,
    CustomSelect,
    FileInput,
    DateInput,
    MultiSelect,
    Checkbox,
    ErrorList,
    NumberInput
} from '@speechki-frontend/shared/ui/forms';
import { LimitsList } from '@speechki-frontend/core/payment/components';
import LanguageSelect from '@speechki-frontend/shared/ui/LanguageSelect';
import SpeakerSelect from '@speechki-frontend/shared/ui/SpeakerSelect';
import TransitionExpand from '@speechki-frontend/shared/ui/TransitionExpand';

import { mapState, mapActions, mapGetters } from 'vuex';

import { format } from 'date-fns';

import { fetchLanguages } from '../../api/books';
import { updateUser, updateAuthor } from '@speechki-frontend/core/auth/api';

import { uploadBook } from '../../services/uploadBook';

function formatDate(date) {
    return format(date, 'yyyy-MM-dd');
}

export default {
    name: 'BookUploadForm',
    components: {
        FormField,
        FileInput,
        ErrorList,
        FormInput,
        CustomSelect,
        Checkbox,
        MultiSelect,
        NumberInput,
        LimitsList,
        LanguageSelect,
        SpeakerSelect,
        TransitionExpand,
        DateInput
    },

    currencies: ['RUB', 'USD'],

    data() {
        return {
            book: {
                name: '',
                document: undefined,
                book_language: undefined,
                // is emotion necessary?
                emotion: undefined,
                speaker: undefined,
                isbnx: '',
                copyright_holder: undefined,
                speed: 1.0,
                is_proofing: false,
                reviewers: [],
                authors: undefined
            },
            limit: undefined,
            profile: {
                first_name: '',
                last_name: '',
                patronymic: '',
                birth_day: '',
                phone: '',
                currency: this.$i18n.locale === 'rus' ? 'RUB' : 'USD'
            },
            author: {
                last_name: '',
                first_name: '',
                patronymic: ''
            },

            reviewers: [],

            showIsbn: false,
            showPseudonym: false,
            stayConfidential: false,

            loading: false,
            loadingText: '',
            error: null,
            generationTimeout: undefined,
            languages: [],
            commonLanguage: undefined
        };
    },

    computed: {
        ...mapState({
            speakers: state => state.settings.speakers,
            authors: state => state.auth.userAuthors,
            limits: state => state.payment.limits,
            copyrightHolders: state => state.contractors.copyrightHolders
        }),
        ...mapGetters([
            'freeLimit',
            'isAdmin',
            'isStaff',
            'personalDataFilled',
            'userCurrency',
            'limitsByCurrency',
            'arrayBookLanguages'
        ]),

        authorSelectComponent() {
            return this.isAdmin || this.isStaff ? 'MultiSelect' : 'CustomSelect';
        },

        filteredLimits() {
            const currency = this.personalDataFilled ? this.userCurrency : this.profile.currency;
            const { book_language: bookLang } = this.book;
            const limits = this.limitsByCurrency[currency];

            if (!bookLang) {
                return limits;
            }

            return limits.filter(({ speakers }) =>
                speakers.some(({ book_language }) => book_language === bookLang)
            );
        },

        bookFieldsValid() {
            const fields = ['document', 'name', 'book_language', 'speaker'];

            if (this.authors.length) {
                fields.push('authors');
            }

            return !!(
                fields.every(field => !!this.book[field]) &&
                this.authorsValid &&
                (this.showIsbn ? !!this.book.isbnx : true)
            );
        },
        authorsValid() {
            if (Array.isArray(this.book.authors)) {
                return !!this.book.authors.length;
            }

            return !!this.book.authors;
        },
        profileValid() {
            const fields = ['first_name', 'last_name', 'phone', 'birth_day'];

            return fields.every(field => !!this.profile[field]);
        },
        valid() {
            if (this.personalDataFilled || this.stayConfidential) {
                return this.bookFieldsValid;
            }

            return this.bookFieldsValid && this.profileValid;
        },

        pseudonymData() {
            if (this.author.first_name && this.author.last_name) {
                return this.author;
            }

            const { first_name, last_name, patronymic } = this.profile;

            return { first_name, last_name, patronymic };
        },
        bookLanguage() {
            return this.book.book_language;
        }
    },

    watch: {
        async limit(limits) {
            const params = { limits, common_language: this.commonLanguage };

            this.book.speaker = null;

            await this.fetchSpeakers(params);
        },
        async commonLanguage(common_language) {
            this.book.speaker = null;

            if (!this.limit) return;

            await this.fetchSpeakers({
                common_language,
                limits: this.limit
            });
        },
        showIsbn(value) {
            if (value) {
                this.$nextTick(() => {
                    this.$refs['isbn-input'].focus();
                });
            } else {
                this.book.isbnx = '';
            }
        }
    },

    async created() {
        this.loading = true;

        const promises = [this.fetchLimits(), this.fetchUserAuthors(), this.fetchLanguages()];

        if (!this.arrayBookLanguages.length) {
            promises.unshift(this.fetchBookLanguages());
        }

        if (this.$isManageApp) {
            promises.unshift(this.fetchCopyrightHolders());
        }

        await Promise.all(promises);

        if (this.authors.length === 1) {
            this.setAuthor(this.authors[0].id);
        }

        this.loading = false;
    },

    beforeDestoy() {
        if (this.generationTimeout) {
            clearTimeout(this.generationTimeout);
        }
    },

    methods: {
        ...mapActions([
            'fetchSpeakers',
            'fetchUserAuthors',
            'fetchUser',
            'fetchLimits',
            'fetchBookLanguages',
            'fetchCopyrightHolders'
        ]),

        setDocument(file) {
            this.book.document = file;
        },

        authorName(author) {
            return `${author.first_name} ${author.last_name}`;
        },

        toggleStayConfidential() {
            if (this.stayConfidential) {
                this.resetProfile();
                this.setAuthor(this.authors[0].id);
                return;
            }

            this.profile.first_name = this.$t('misc.anonimous');
            this.profile.last_name = Math.random()
                .toString(36)
                .substring(7);
            this.profile.phone = '10000000000';
            this.profile.birth_day = formatDate(new Date(1976, 0, 1, 0, 0, 1));

            this.author.first_name = this.profile.first_name;
            this.author.last_name = this.profile.last_name;

            this.stayConfidential = true;
        },

        setAuthor(author) {
            this.book.authors = author;

            if (this.isStaff || this.isAdmin) {
                this.book.authors = [author];
            }
        },

        resetProfile() {
            this.profile.first_name = '';
            this.profile.last_name = '';
            this.profile.phone = '';
            this.profile.birth_day = undefined;
            this.profile.phone = '';
            this.stayConfidential = false;

            this.author.last_name = '';
            this.author.first_name = '';
        },

        copyPseudo() {
            const { last_name, first_name, patronymic } = this.author;
            this.author = { last_name, first_name, patronymic };
            this.showPseudonym = false;
            this.stayConfidential = false;
        },

        async uploadBook() {
            if (!this.valid) {
                this.error = [{ detail: 'Please fill all the fields' }];
                return;
            }

            try {
                this.error = null;
                this.loading = true;
                this.loadingText = 'Creating the book';

                if (!this.personalDataFilled) {
                    await Promise.all([
                        updateUser({
                            ...this.profile,
                            phone: this.profile.phone.replace(/[+()-]/g, '')
                        }),
                        updateAuthor(this.authors[0].id, this.pseudonymData)
                    ]);
                    await this.fetchUser();
                }

                const { id } = await uploadBook(
                    this.book,
                    this.$isManageApp,
                    ({ completed, total }) => {
                        this.loadingText = `Generating sentence ${completed} of ${total}`;
                    }
                );

                this.loading = false;

                this.$router.push({
                    name: 'editor',
                    params: { id },
                    query: { page: 1 }
                });
            } catch (e) {
                this.error = e;
                this.loading = false;
                console.log(e);
            }
        },

        async onReviewersSearch(searchString) {
            try {
                const { results } = await this.$http.get('/api/v1/admin/users_info/users/', {
                    params: {
                        search: searchString
                    }
                });
                this.reviewers = results;
            } catch (e) {
                console.log(e);
            }
        },

        async fetchLanguages() {
            this.languages = await fetchLanguages();
        },

        onLanguageUpdate({ slug, item }) {
            this.book.book_language = slug;
            this.commonLanguage = item.slug;
            this.book.speaker = null;
        }
    }
};
</script>

<style lang="scss" src="./BookUploadForm.scss"></style>
